// Lightweight IndexedDB store for in-flight Mileage trip snapshots.
// Persists every GPS update so a crashed tab / killed browser can
// still recover the trip on next launch (survives localStorage wipes too).

const DB_NAME = 'maidly_mileage';
const STORE = 'snapshots';
const DB_VERSION = 1;

const openDB = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not supported'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/** Persist a full trip snapshot (idempotent per trip.id). */
export const saveTripSnapshot = async (trip) => {
  if (!trip || !trip.id) return;
  try {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ ...trip, _savedAt: Date.now() });
    await new Promise((res) => {
      tx.oncomplete = res;
      tx.onerror = res;
      tx.onabort = res;
    });
  } catch (e) {
    // Best-effort: snapshot failures are non-fatal (private mode, quota, disabled IDB)
    console.warn('[tripSnapshotDB] saveTripSnapshot failed:', e);
  }
};

/** Load the most recently saved trip snapshot, or null. */
export const loadLatestTripSnapshot = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    return await new Promise((resolve) => {
      req.onsuccess = () => {
        const rows = req.result || [];
        if (!rows.length) return resolve(null);
        rows.sort((a, b) => (b._savedAt || 0) - (a._savedAt || 0));
        resolve(rows[0]);
      };
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
};

/** Remove a snapshot by id (or all if id is falsy). */
export const clearTripSnapshot = async (id) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readwrite');
    if (id) tx.objectStore(STORE).delete(id);
    else tx.objectStore(STORE).clear();
    await new Promise((res) => {
      tx.oncomplete = res;
      tx.onerror = res;
      tx.onabort = res;
    });
  } catch (e) {
    // Best-effort: clearing failure is non-fatal
    console.warn('[tripSnapshotDB] clearTripSnapshot failed:', e);
  }
};
